/**
 * Dialog para seleção e envio de Template WhatsApp
 * 
 * Duas etapas:
 * 1. Seleção: Lista templates aprovados com busca e sincronização
 * 2. Preenchimento: Inputs para variáveis do BODY e dos BOTÕES separadamente
 * 
 * IMPORTANTE: Variáveis do BODY são independentes das variáveis dos BOTÕES
 * Cada botão dinâmico tem sua própria variável {{1}}
 * 
 * SUPORTE A BOTÕES:
 * - URL dinâmica: Botões com {{1}} na URL para sufixo variável
 * - ORDER_DETAILS: Botões de pagamento PIX com estrutura completa
 * - QUICK_REPLY: Botões de resposta rápida (não requerem parâmetros)
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Search,
  RefreshCw,
  FileText,
  ChevronLeft,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  XCircle,
  Globe,
  ExternalLink,
  CreditCard,
  QrCode,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TemplateCard } from '@/components/tenant/templates/TemplateCard';

/**
 * Interface para template WhatsApp
 */
interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  template_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  created_at?: string;
  components: any;
  variables: any;
  classification_id?: string | null;
  /** URL permanente da mídia do header armazenada no Supabase Storage */
  header_media_url?: string | null;
}

/**
 * Interface para botão dinâmico URL detectado
 */
interface DynamicButton {
  index: number;
  text: string;
  url: string;
  baseUrl: string; // URL sem o {{1}}
}

/**
 * Interface para botão ORDER_DETAILS (PIX) detectado
 */
interface OrderDetailsButton {
  index: number;
  text: string;
}

/**
 * Interface para parâmetros PIX do botão ORDER_DETAILS
 */
export interface OrderDetailsParams {
  pix_code: string;           // Código PIX copia-cola
  merchant_name: string;      // Nome do recebedor
  pix_key: string;            // Chave PIX
  pix_key_type: 'CNPJ' | 'CPF' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';
  amount_value: number;       // Valor em centavos
  item_name?: string;         // Descrição do item
  reference_id?: string;      // ID único da cobrança
}

interface SendTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboxId: string;
  /** Callback para enviar template com variáveis preenchidas */
  onSendTemplate: (params: {
    template_name: string;
    template_language: string;
    template_params: Array<{ type: string; text?: string }>;
    /** Parâmetros dos botões dinâmicos URL (separados do body) */
    button_params?: Array<{
      type: 'button';
      sub_type: 'url';
      index: number;
      parameters: Array<{ type: 'text'; text: string }>;
    }>;
    /** Parâmetros PIX para botões ORDER_DETAILS */
    order_details_params?: OrderDetailsParams;
    /** Botões do template para visualização no chat */
    template_buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>;
    /** Texto do body com variáveis substituídas para exibição no chat */
    template_body: string;
    /** Formato do header de mídia (IMAGE, VIDEO, DOCUMENT) */
    header_format?: string;
    /** URL da mídia do header */
    header_media_url?: string;
  }) => Promise<void>;
}

/**
 * Extrai componente por tipo do array de components
 */
const getComponent = (components: any[], type: string) => {
  if (!components || !Array.isArray(components)) return null;
  return components.find(c => c.type === type);
};

/**
 * Extrai variáveis do texto do body (formato {{1}}, {{2}}, etc.)
 */
const extractVariables = (text: string): string[] => {
  if (!text) return [];
  const matches = text.match(/\{\{(\d+)\}\}/g);
  if (!matches) return [];
  // Remove duplicatas e ordena
  return [...new Set(matches)].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''));
    const numB = parseInt(b.replace(/\D/g, ''));
    return numA - numB;
  });
};

/**
 * Substitui variáveis pelos valores fornecidos
 */
const replaceVariables = (text: string, values: Record<string, string>): string => {
  if (!text) return '';
  let result = text;
  Object.entries(values).forEach(([key, value]) => {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || `{{${key}}}`);
  });
  return result;
};

export function SendTemplateDialog({
  open,
  onOpenChange,
  inboxId,
  onSendTemplate,
}: SendTemplateDialogProps) {
  // Estados
  const [step, setStep] = useState<'select' | 'fill'>('select');
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  
  // Estado para variáveis do BODY
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  
  // Estado para variáveis dos BOTÕES URL dinâmicos (separado do body)
  const [buttonValues, setButtonValues] = useState<Record<number, string>>({});
  
  // Estado para parâmetros PIX (ORDER_DETAILS)
  const [pixParams, setPixParams] = useState<Partial<OrderDetailsParams>>({
    pix_key_type: 'CNPJ',
  });

  // Busca templates quando abre
  useEffect(() => {
    if (open && inboxId) {
      fetchTemplates();
    }
  }, [open, inboxId]);

  // Reset quando fecha
  useEffect(() => {
    if (!open) {
      setStep('select');
      setSelectedTemplate(null);
      setVariableValues({});
      setButtonValues({});
      setPixParams({ pix_key_type: 'CNPJ' });
      setSearchQuery('');
    }
  }, [open]);

  /**
   * Busca templates aprovados da inbox
   */
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .eq('inbox_id', inboxId)
        .eq('status', 'APPROVED')
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('[SendTemplateDialog] Erro ao buscar templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sincroniza templates com o provider
   */
  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-templates', {
        body: { inbox_id: inboxId },
      });

      if (error) throw error;
      
      toast.success(`Templates sincronizados: ${data?.synced || 0}`);
      await fetchTemplates();
    } catch (error) {
      console.error('[SendTemplateDialog] Erro ao sincronizar:', error);
      toast.error('Erro ao sincronizar templates');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Handler para selecionar template
   */
  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    setVariableValues({});
    setButtonValues({});
    setPixParams({ pix_key_type: 'CNPJ' });
    setStep('fill');
  };

  /**
   * Handler para voltar à seleção
   */
  const handleBack = () => {
    setStep('select');
    setSelectedTemplate(null);
    setVariableValues({});
    setButtonValues({});
    setPixParams({ pix_key_type: 'CNPJ' });
  };

  /**
   * Handler para enviar template
   */
  const handleSend = async () => {
    if (!selectedTemplate) return;

    // Verifica se todas as variáveis do BODY foram preenchidas
    const missingBodyVars = bodyVariables.filter(v => {
      const key = v.replace(/[{}]/g, '');
      return !variableValues[key]?.trim();
    });

    if (missingBodyVars.length > 0) {
      toast.error('Preencha todas as variáveis da mensagem');
      return;
    }

    // Verifica se todas as variáveis dos BOTÕES URL foram preenchidas
    const missingButtonVars = dynamicButtons.filter(btn => !buttonValues[btn.index]?.trim());
    if (missingButtonVars.length > 0) {
      toast.error('Preencha todas as variáveis dos botões');
      return;
    }

    // Verifica se os parâmetros PIX foram preenchidos (se houver botão ORDER_DETAILS)
    if (orderDetailsButtons.length > 0) {
      if (!pixParams.pix_code?.trim()) {
        toast.error('Preencha o código PIX copia-cola');
        return;
      }
      if (!pixParams.merchant_name?.trim()) {
        toast.error('Preencha o nome do recebedor');
        return;
      }
      if (!pixParams.pix_key?.trim()) {
        toast.error('Preencha a chave PIX');
        return;
      }
      if (!pixParams.amount_value || pixParams.amount_value <= 0) {
        toast.error('Preencha o valor do pagamento');
        return;
      }
    }

    setSending(true);
    try {
      // Monta params do BODY na ordem correta (1, 2, 3...)
      const bodyParams = bodyVariables.map(v => {
        const key = v.replace(/[{}]/g, '');
        return { type: 'text', text: variableValues[key] };
      });

      // Monta params dos BOTÕES URL (separados)
      const buttonParams = dynamicButtons.map(btn => ({
        type: 'button' as const,
        sub_type: 'url' as const,
        index: btn.index,
        parameters: [{ type: 'text' as const, text: buttonValues[btn.index] || '' }],
      }));

      // Monta params PIX se houver botão ORDER_DETAILS
      let orderDetailsParams: OrderDetailsParams | undefined;
      if (orderDetailsButtons.length > 0 && pixParams.pix_code) {
        orderDetailsParams = {
          pix_code: pixParams.pix_code,
          merchant_name: pixParams.merchant_name || '',
          pix_key: pixParams.pix_key || '',
          pix_key_type: pixParams.pix_key_type || 'CNPJ',
          amount_value: Math.round((pixParams.amount_value || 0) * 100), // Converte para centavos
          item_name: pixParams.item_name || 'Pagamento',
          reference_id: pixParams.reference_id || crypto.randomUUID(),
        };
      }

      // Extrai botões do template para visualização no chat
      const buttonsComp = getComponent(selectedTemplate.components, 'BUTTONS');
      const templateButtons = buttonsComp?.buttons?.map((btn: any) => ({
        type: btn.type,
        text: btn.text || '',
        url: btn.url,
        phone_number: btn.phone_number,
      })) || [];

      // Extrai informações do header de mídia para salvar no metadata
      // Prioriza URL permanente do Supabase Storage sobre URL temporária da Meta (header_handle)
      const headerComp = getComponent(selectedTemplate.components, 'HEADER');
      const headerFormat = headerComp?.format || 'NONE';
      const headerMediaUrl = selectedTemplate.header_media_url || headerComp?.example?.header_handle?.[0] || '';

      await onSendTemplate({
        template_name: selectedTemplate.name,
        template_language: selectedTemplate.language,
        template_params: bodyParams,
        button_params: buttonParams.length > 0 ? buttonParams : undefined,
        order_details_params: orderDetailsParams,
        template_buttons: templateButtons.length > 0 ? templateButtons : undefined,
        template_body: previewText || bodyText,
        // Dados do header de mídia para renderização no chat
        header_format: headerFormat !== 'NONE' && headerFormat !== 'TEXT' ? headerFormat : undefined,
        header_media_url: headerMediaUrl || undefined,
      });

      toast.success('Template enviado com sucesso!');
      onOpenChange(false);
    } catch (error) {
      console.error('[SendTemplateDialog] Erro ao enviar:', error);
      toast.error('Erro ao enviar template');
    } finally {
      setSending(false);
    }
  };

  // Filtra templates pela busca
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => 
      t.name.toLowerCase().includes(query) ||
      t.category.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  // Extrai body text do template selecionado
  const bodyText = useMemo(() => {
    if (!selectedTemplate?.components) return '';
    const body = getComponent(selectedTemplate.components, 'BODY');
    return body?.text || '';
  }, [selectedTemplate]);

  // Preview do body com variáveis substituídas
  const previewText = useMemo(() => {
    return replaceVariables(bodyText, variableValues);
  }, [bodyText, variableValues]);

  // Lista de variáveis do BODY
  const bodyVariables = useMemo(() => {
    return extractVariables(bodyText);
  }, [bodyText]);

  // Detecta botões dinâmicos URL (com {{1}})
  const dynamicButtons = useMemo((): DynamicButton[] => {
    if (!selectedTemplate?.components) return [];
    const buttonsComp = getComponent(selectedTemplate.components, 'BUTTONS');
    if (!buttonsComp?.buttons) return [];
    
    return buttonsComp.buttons
      .map((btn: any, index: number) => {
        const isDynamic = btn.type === 'URL' && btn.url?.includes('{{1}}');
        if (!isDynamic) return null;
        
        return {
          index,
          text: btn.text || `Botão ${index + 1}`,
          url: btn.url,
          baseUrl: btn.url.replace('{{1}}', ''),
        };
      })
      .filter(Boolean) as DynamicButton[];
  }, [selectedTemplate]);

  // Detecta botões ORDER_DETAILS (PIX)
  const orderDetailsButtons = useMemo((): OrderDetailsButton[] => {
    if (!selectedTemplate?.components) return [];
    const buttonsComp = getComponent(selectedTemplate.components, 'BUTTONS');
    if (!buttonsComp?.buttons) return [];
    
    return buttonsComp.buttons
      .map((btn: any, index: number) => {
        if (btn.type !== 'ORDER_DETAILS') return null;
        return {
          index,
          text: btn.text || 'Ver Detalhes do Pedido',
        };
      })
      .filter(Boolean) as OrderDetailsButton[];
  }, [selectedTemplate]);

  // Configurações de status para o TemplateCard
  const statusConfig = {
    APPROVED: { label: 'Aprovado', icon: CheckCircle2, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
    PENDING: { label: 'Pendente', icon: Clock, color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
    REJECTED: { label: 'Rejeitado', icon: XCircle, color: 'bg-red-500/10 text-red-600 border-red-500/20' },
  };

  // Configurações de categoria para o TemplateCard
  const categoryConfig = {
    MARKETING: { label: 'Marketing', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
    UTILITY: { label: 'Utilitário', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
    AUTHENTICATION: { label: 'Autenticação', color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  };

  // Cores por categoria (mantido para etapa fill)
  const categoryColors: Record<string, string> = {
    MARKETING: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
    UTILITY: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    AUTHENTICATION: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  };

  // Labels em português para categorias
  const categoryLabels: Record<string, string> = {
    UTILITY: 'Utilitário',
    MARKETING: 'Marketing',
    AUTHENTICATION: 'Autenticação',
  };

  // Total de parâmetros a preencher
  const totalParams = bodyVariables.length + dynamicButtons.length + (orderDetailsButtons.length > 0 ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {step === 'select' ? 'Selecionar Template' : `Enviar: ${selectedTemplate?.name}`}
          </DialogTitle>
        </DialogHeader>

        {/* Etapa 1: Seleção de Template */}
        {step === 'select' && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Header com busca e sync */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={handleSync}
                disabled={syncing}
              >
                <RefreshCw className={cn("h-4 w-4", syncing && "animate-spin")} />
              </Button>
            </div>

            {/* Lista de templates */}
            <ScrollArea className="h-[calc(70vh-180px)]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mb-2" />
                  <p className="font-medium">Nenhum template encontrado</p>
                  <p className="text-sm">
                    {templates.length === 0 
                      ? 'Sincronize os templates do WhatsApp Business' 
                      : 'Tente outra busca'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 pb-4 px-1">
                  {filteredTemplates.map(template => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className="cursor-pointer"
                    >
                      <TemplateCard
                        template={{
                          ...template,
                          rejection_reason: null,
                          example_values: null,
                          provider_metadata: null,
                          created_at: template.created_at || new Date().toISOString(),
                          submitted_at: null,
                          approved_at: null,
                          classification_id: template.classification_id || null,
                        }}
                        statusConfig={statusConfig}
                        categoryConfig={categoryConfig}
                        onEdit={() => {}}
                        onPreview={() => handleSelectTemplate(template)}
                        onDelete={() => {}}
                        canEdit={false}
                        canDelete={false}
                      />
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {/* Etapa 2: Preenchimento de Variáveis */}
        {step === 'fill' && selectedTemplate && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Header com badges - mesmo padrão da página 1 */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-muted/30">
                  <Badge 
                    variant="outline" 
                    className={cn("text-[10px] shrink-0 px-2 py-0.5", categoryColors[selectedTemplate.category])}
                  >
                    {categoryLabels[selectedTemplate.category] || selectedTemplate.category}
                  </Badge>
                  <div className="flex items-center gap-1 text-muted-foreground shrink-0">
                    <Globe className="h-3 w-3" />
                    <span className="text-xs">{selectedTemplate.language}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0 px-2 py-0.5 bg-green-500/10 text-green-600 border-green-500/20">
                    <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                    APROVADO
                  </Badge>
                </div>
              </div>
            </div>

            {/* Grid com 2 colunas - altura flexível com scroll na coluna de variáveis */}
            <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-hidden">
              {/* Coluna: Preview - altura fixa proporcional ao texto */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium shrink-0">Preview da Mensagem</Label>
                <div className="rounded-xl bg-[#e5ddd5] p-3 max-h-[280px] overflow-auto">
                  <div className="max-w-[220px]">
                    <div className="bg-white rounded-lg shadow-sm p-2">
                      {/* Preview do header de mídia (VIDEO/IMAGE/DOCUMENT) */}
                      {(() => {
                        const hComp = selectedTemplate ? getComponent(selectedTemplate.components, 'HEADER') : null;
                        const hFormat = hComp?.format;
                        // Priorizar header_media_url permanente sobre header_handle temporário
                        const hUrl = (selectedTemplate as any)?.header_media_url || hComp?.example?.header_handle?.[0];
                        if (hFormat === 'VIDEO' && hUrl) {
                          return (
                            <div className="mb-1.5 rounded overflow-hidden bg-black/5">
                              <video src={hUrl} controls className="w-full max-h-[120px] object-contain" />
                            </div>
                          );
                        }
                        if (hFormat === 'IMAGE' && hUrl) {
                          return (
                            <div className="mb-1.5 rounded overflow-hidden">
                              <img src={hUrl} alt="Header" className="w-full max-h-[120px] object-cover rounded" />
                            </div>
                          );
                        }
                        if (hFormat === 'DOCUMENT' && hUrl) {
                          return (
                            <div className="mb-1.5 flex items-center gap-1.5 p-1.5 bg-muted/50 rounded text-xs">
                              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="truncate text-muted-foreground">Documento anexado</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <p className="text-xs whitespace-pre-wrap break-words">
                        {previewText || bodyText}
                      </p>
                      <div className="text-right mt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {new Date().toLocaleTimeString('pt-BR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coluna: Variáveis - scroll quando muitos parâmetros */}
              <div className="flex flex-col gap-2 min-h-0 flex-1">
                <Label className="text-sm font-medium shrink-0">
                  Variáveis {totalParams > 0 && `(${totalParams})`}
                </Label>
                <ScrollArea className="flex-1 min-h-0">
                  <div className="pr-3 space-y-4">
                    {/* Variáveis do BODY */}
                    {bodyVariables.length > 0 && (
                      <div className="space-y-3">
                        <div className="text-xs text-muted-foreground font-medium">
                          Mensagem
                        </div>
                        {bodyVariables.map(variable => {
                          const key = variable.replace(/[{}]/g, '');
                          return (
                            <div key={key} className="space-y-1">
                              <Label htmlFor={`var-${key}`} className="text-xs text-muted-foreground">
                                Valor para {variable}
                              </Label>
                              <Input
                                id={`var-${key}`}
                                placeholder={`Digite o valor para ${variable}`}
                                value={variableValues[key] || ''}
                                onChange={(e) => setVariableValues(prev => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Variáveis dos BOTÕES URL dinâmicos (separadas) */}
                    {dynamicButtons.length > 0 && (
                      <div className="space-y-3">
                        {bodyVariables.length > 0 && <Separator />}
                        <div className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" />
                          Botões Dinâmicos (URL)
                        </div>
                        {dynamicButtons.map(btn => (
                          <div key={btn.index} className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              "{btn.text}"
                            </Label>
                            <div className="text-[10px] text-muted-foreground font-mono truncate">
                              {btn.baseUrl}<span className="text-foreground">{'{{1}}'}</span>
                            </div>
                            <Input
                              placeholder="Valor da variável do botão"
                              value={buttonValues[btn.index] || ''}
                              onChange={(e) => setButtonValues(prev => ({
                                ...prev,
                                [btn.index]: e.target.value,
                              }))}
                            />
                            {buttonValues[btn.index] && (
                              <div className="text-[10px] text-muted-foreground">
                                URL final: <span className="font-mono text-foreground">{btn.baseUrl}{buttonValues[btn.index]}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Parâmetros PIX (ORDER_DETAILS) */}
                    {orderDetailsButtons.length > 0 && (
                      <div className="space-y-3">
                        {(bodyVariables.length > 0 || dynamicButtons.length > 0) && <Separator />}
                        <div className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                          <QrCode className="h-3 w-3" />
                          Botão de Pagamento PIX
                        </div>
                        
                        {/* Código PIX */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            Código PIX Copia-Cola *
                          </Label>
                          <Input
                            placeholder="Cole o código PIX aqui"
                            value={pixParams.pix_code || ''}
                            onChange={(e) => setPixParams(prev => ({ ...prev, pix_code: e.target.value }))}
                          />
                        </div>
                        
                        {/* Nome do Recebedor */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            Nome do Recebedor *
                          </Label>
                          <Input
                            placeholder="Ex: Minha Empresa LTDA"
                            value={pixParams.merchant_name || ''}
                            onChange={(e) => setPixParams(prev => ({ ...prev, merchant_name: e.target.value }))}
                          />
                        </div>
                        
                        {/* Chave PIX + Tipo */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              Chave PIX *
                            </Label>
                            <Input
                              placeholder="CNPJ, CPF, Email..."
                              value={pixParams.pix_key || ''}
                              onChange={(e) => setPixParams(prev => ({ ...prev, pix_key: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              Tipo *
                            </Label>
                            <Select
                              value={pixParams.pix_key_type || 'CNPJ'}
                              onValueChange={(value) => setPixParams(prev => ({ 
                                ...prev, 
                                pix_key_type: value as OrderDetailsParams['pix_key_type'] 
                              }))}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CNPJ">CNPJ</SelectItem>
                                <SelectItem value="CPF">CPF</SelectItem>
                                <SelectItem value="EMAIL">E-mail</SelectItem>
                                <SelectItem value="CELULAR">Celular</SelectItem>
                                <SelectItem value="ALEATORIA">Aleatória</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Valor */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            Valor (R$) *
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            placeholder="0,00"
                            value={pixParams.amount_value || ''}
                            onChange={(e) => setPixParams(prev => ({ 
                              ...prev, 
                              amount_value: parseFloat(e.target.value) || 0 
                            }))}
                          />
                        </div>
                        
                        {/* Descrição do Item (opcional) */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            Descrição (opcional)
                          </Label>
                          <Input
                            placeholder="Ex: Mensalidade Plano Pro"
                            value={pixParams.item_name || ''}
                            onChange={(e) => setPixParams(prev => ({ ...prev, item_name: e.target.value }))}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Estado vazio */}
                    {totalParams === 0 && (
                      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                        Este template não possui variáveis
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>

            <Separator />

            {/* Footer com ações */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={handleBack}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Voltar
              </Button>
              <Button onClick={handleSend} disabled={sending}>
                {sending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Enviar Mensagem
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
